// Radha — Contracts panel, on the direct path. Third module from the template.
//
// Panel only; DirectShell owns the header, tabs and sign-out. Two surfaces: the
// contract ledger (with live headroom) and the upcoming-renewals/churn panel.
import React, { useMemo } from "react";

import { useContracts, useRenewals } from "../hooks/useContractsDirect";
import { ModuleProposals } from "../components/ModuleProposals";
import { ModuleChart } from "../components/ModuleChart";
import { useTableSearch, SearchBar, ExportButton } from "../components/TableSearch";
import { fmtMoney as inr } from "../lib/money";


const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 500 };
const td: React.CSSProperties = { padding: "10px 8px" };
const numTd: React.CSSProperties = { ...td, textAlign: "right", whiteSpace: "nowrap" };
const statusLabel = (s: string) => s.replace(/_/g, " ");
const statusTd: React.CSSProperties = { ...td, textTransform: "capitalize" };
const pill = (c: string): React.CSSProperties => ({
  marginLeft: 6, padding: "1px 6px", fontSize: 11, color: c,
  border: `1px solid ${c}`, borderRadius: 6,
});

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ color: "var(--fg-muted)", fontSize: 12 }}>{label}</div>
      <div style={{ font: "600 20px Inter" }}>{value}</div>
    </div>
  );
}

function churnColor(p: number): string {
  if (p >= 0.66) return "var(--status-critical)";
  if (p >= 0.4) return "var(--status-warning)";
  return "var(--status-good)";
}

export function ContractsPanel() {
  const contracts = useContracts();
  const renewals = useRenewals();

  const totals = useMemo(() => {
    const list = contracts.data ?? [];
    return {
      count: list.length,
      acv: list.reduce((s, c) => s + Number(c.acv_amount), 0),
      headroom: list.reduce((s, c) => s + Number(c.headroom), 0),
      autoRenew: list.filter((c) => c.auto_renew).length,
    };
  }, [contracts.data]);

  const contractName = useMemo(() => {
    const m = new Map<string, string>();
    (contracts.data ?? []).forEach((c) => m.set(c.id, c.customer_name));
    return m;
  }, [contracts.data]);

  const { q, setQ, filtered } = useTableSearch(contracts.data,
    (c) => `${c.contract_number} ${c.customer_name} ${c.status}`);

  if (contracts.isLoading) return <p style={{ padding: 24 }}>Loading contracts…</p>;
  if (contracts.error) return <p style={{ padding: 24, color: "var(--status-critical)" }}>{(contracts.error as Error).message}</p>;

  return (
    <div>
      <div style={{ display: "flex", gap: 24, margin: "4px 0 20px", flexWrap: "wrap" }}>
        <Stat label="Contracts" value={String(totals.count)} />
        <Stat label="Total ACV" value={inr(totals.acv)} />
        <Stat label="Headroom (un-invoiced)" value={inr(totals.headroom)} />
        <Stat label="Auto-renew" value={String(totals.autoRenew)} />
      </div>

      <ModuleChart title="ACV by status" money color="var(--accent-purple)"
        data={[...(contracts.data ?? []).reduce((m, c) => m.set(c.status, (m.get(c.status) ?? 0) + Number(c.acv_amount)), new Map<string, number>())]
          .map(([label, value]) => ({ label, value }))} />

      <ModuleProposals table="contract_proposals" />

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <SearchBar q={q} setQ={setQ} count={filtered.length} total={(contracts.data ?? []).length} placeholder="Search contract / customer…" />
        <ExportButton name="Contracts" rows={() => filtered.map((c) => ({
          Contract: c.contract_number, Customer: c.customer_name, ACV: Number(c.acv_amount),
          Invoiced: Number(c.invoiced_amount), Headroom: Number(c.headroom),
          "Auto-renew": c.auto_renew ? "yes" : "no", Status: c.status,
        }))} />
      </div>

      <div className="sanctuary-scroll-x">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12 }}>
              <th style={th}>Contract</th><th style={th}>Customer</th><th style={{ ...th, textAlign: "right" }}>ACV</th>
              <th style={{ ...th, textAlign: "right" }}>Invoiced</th><th style={{ ...th, textAlign: "right" }}>Headroom</th><th style={th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={td}>{c.contract_number}</td>
                <td style={td}>
                  {c.customer_name}
                  {c.auto_renew && <span style={pill("var(--status-good)")}>auto</span>}
                </td>
                <td style={numTd}>{inr(c.acv_amount)}</td>
                <td style={numTd}>{inr(c.invoiced_amount)}</td>
                <td style={numTd}>{inr(c.headroom)}</td>
                <td style={statusTd}>{statusLabel(c.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 style={{ font: "600 15px Inter", margin: "28px 0 8px" }}>Upcoming renewals</h3>
      {renewals.isLoading ? (
        <p style={{ color: "var(--fg-muted)" }}>loading…</p>
      ) : (renewals.data ?? []).length === 0 ? (
        <p style={{ color: "var(--fg-muted)" }}>No renewals scheduled.</p>
      ) : (
        <div className="sanctuary-scroll-x">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--fg-muted)", fontSize: 12 }}>
              <th style={th}>Customer</th><th style={th}>Renewal date</th><th style={th}>Status</th>
              <th style={{ ...th, textAlign: "right" }}>Churn</th><th style={{ ...th, textAlign: "right" }}>Proposed ACV</th>
            </tr>
          </thead>
          <tbody>
            {(renewals.data ?? []).map((r) => (
              <tr key={r.id} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={td}>{contractName.get(r.contract_id) ?? "—"}</td>
                <td style={td}>{r.renewal_date}</td>
                <td style={statusTd}>{statusLabel(r.status)}</td>
                <td style={{ ...numTd, color: churnColor(Number(r.churn_probability)) }}>
                  {Math.round(Number(r.churn_probability) * 100)}%
                </td>
                <td style={numTd}>{inr(r.proposed_acv)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}
